import { useState } from "react";
import { useDispatch } from "react-redux";
import { User, UserCircle, Palette, Eye, EyeOff, ShieldCheck } from "lucide-react";
import FileUpload from "../../../pages/user/branding-file-upload";
import AgreementSection from "../../agreement-section/agreement-section.component";
import { getApiUrl } from "../../../utils/api";
import { setCurrentUser } from "../../../redux/user/user.actions";

const InfoRow = ({ label, value, sensitive = false }) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
        <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 w-24 flex-shrink-0">{label}</span>
            {sensitive ? (
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900 font-medium">
                        {isVisible ? value : (value ? '••••••••' : null)}
                    </span>
                    {value && (
                        <button
                            onClick={() => setIsVisible(!isVisible)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            aria-label={isVisible ? "Hide value" : "Show value"}
                        >
                            {isVisible ? (
                                <EyeOff className="w-3.5 h-3.5 text-gray-400" />
                            ) : (
                                <Eye className="w-3.5 h-3.5 text-gray-400" />
                            )}
                        </button>
                    )}
                </div>
            ) : (
                <span className="text-sm text-gray-900 font-medium truncate">
                    {value || <span className="text-gray-400 font-normal">Not provided</span>}
                </span>
            )}
        </div>
    );
};

const Toggle = ({ enabled, onChange, disabled }) => (
    <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={disabled}
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed ${
            enabled ? "bg-primary" : "bg-gray-200"
        }`}
    >
        <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                enabled ? "translate-x-5" : "translate-x-0"
            }`}
        />
    </button>
);

const AccountInfoAdvisor = ({ currentUser, info }) => {
    const dispatch = useDispatch();
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(info?.twoFactorEnabled ?? currentUser?.twoFactorEnabled ?? false);
    const [twoFactorLoading, setTwoFactorLoading] = useState(false);
    const [twoFactorMessage, setTwoFactorMessage] = useState(null);

    const apiUrl = getApiUrl();

    const handleToggle2FA = async (newValue) => {
        setTwoFactorLoading(true);
        setTwoFactorMessage(null);
        try {
                        const response = await fetch(`${apiUrl}/users/twofactor/${currentUser.id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${currentUser.token}`,
                },
                body: JSON.stringify({ enabled: newValue }),
            });

            if (!response.ok) {
                const data = await response.json();
                setTwoFactorMessage({ text: data.message || "Failed to update setting.", error: true });
                return;
            }

            setTwoFactorEnabled(newValue);
            setTwoFactorMessage({
                text: newValue
                    ? "Two-factor authentication enabled."
                    : "Two-factor authentication disabled.",
                error: false,
            });
            dispatch(setCurrentUser({ ...currentUser, twoFactorEnabled: newValue }));
        } catch {
            setTwoFactorMessage({ text: "An error occurred. Please try again.", error: true });
        } finally {
            setTwoFactorLoading(false);
        }
    };

    return (
        <div className="flex-1 px-6 md:px-12 lg:pl-12 lg:pr-24 py-6 pb-5">
            {/* page header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-xl">
                    <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Profile</h1>
                    <p className="text-sm text-gray-500">Manage your account settings</p>
                </div>
            </div>

            <div className="space-y-6 max-w-3xl">
                {/* advisor details card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <UserCircle className="w-5 h-5 text-primary" />
                        </div>
                        <h3 className="font-semibold text-gray-900">Advisor Information</h3>
                    </div>
                    <div className="p-5">
                        <div className="space-y-2.5">
                            <InfoRow
                                label="Name"
                                value={info?.name || `${currentUser.firstName} ${currentUser.lastName}`}
                            />
                            <InfoRow label="Advisor ID" value={info?.id || currentUser.id} />
                            <InfoRow label="Email" value={info?.email || currentUser.email} />
                            <InfoRow label="Description" value={info?.description || currentUser.description} />
                        </div>
                    </div>
                </div>

                {/* security card */}
                {(!currentUser?.opsActor || currentUser.opsScopeKind === 'user') && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <ShieldCheck className="w-5 h-5 text-primary" />
                        </div>
                        <h3 className="font-semibold text-gray-900">Security</h3>
                    </div>
                    <div className="p-5">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <p className="text-sm font-medium text-gray-900">Two-factor authentication</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    When enabled, you'll be asked to verify your identity via email on unrecognized devices.
                                </p>
                                {twoFactorMessage && (
                                    <p className={`text-xs mt-1.5 font-medium ${twoFactorMessage.error ? "text-red-500" : "text-green-600"}`}>
                                        {twoFactorMessage.text}
                                    </p>
                                )}
                            </div>
                            <Toggle
                                enabled={twoFactorEnabled}
                                onChange={handleToggle2FA}
                                disabled={twoFactorLoading || !!currentUser?.opsActor}
                            />
                        </div>
                    </div>
                </div>
                )}

                {/* branding card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <Palette className="w-5 h-5 text-primary" />
                        </div>
                        <h3 className="font-semibold text-gray-900">Branding</h3>
                    </div>
                    <div className="p-5">
                        <FileUpload />
                    </div>
                </div>

                {/* Advisor Agreement — self-hides when none on file */}
                <AgreementSection />
            </div>
        </div>
    );
};

export default AccountInfoAdvisor;